import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { Cashback } from 'services/interfaces/cashback';

interface CashbackPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: Cashback | null;
  onConfirm: (payload: { paid: boolean; payment_date: string }) => void;
  submitting: boolean;
}

function toInputDate(iso?: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

const CashbackPaymentModal: React.FC<CashbackPaymentModalProps> = ({
  isOpen,
  onClose,
  row,
  onConfirm,
  submitting,
}) => {
  const [isPaid, setIsPaid] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');

  useEffect(() => {
    if (!row || !isOpen) return;
    if (row.paid) {
      setIsPaid(true);
      setPaymentDate(toInputDate(row.payment_date) || '');
    } else {
      setIsPaid(true);
      setPaymentDate('');
    }
  }, [row, isOpen]);

  const handleSubmit = () => {
    if (isPaid && !paymentDate.trim()) {
      return;
    }
    onConfirm({
      paid: isPaid,
      payment_date: isPaid ? paymentDate : '',
    });
  };

  const canSubmit = !isPaid || Boolean(paymentDate.trim());

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent mx={3}>
        <ModalHeader>Pagamento de cashback</ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody>
          {row && (
            <Stack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                {row.company_name} · IDI {row.idi}
              </Text>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} htmlFor="cashback-paid">
                  Marcado como pago
                </FormLabel>
                <Switch
                  id="cashback-paid"
                  isChecked={isPaid}
                  isDisabled={submitting}
                  onChange={(e) => setIsPaid(e.target.checked)}
                  colorScheme="yooga"
                />
              </FormControl>
              <FormControl isRequired={isPaid}>
                <FormLabel>Data de pagamento</FormLabel>
                <Input
                  type="date"
                  value={paymentDate}
                  isDisabled={submitting || !isPaid}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
                {isPaid && !paymentDate.trim() && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    Informe a data de pagamento para concluir.
                  </Text>
                )}
              </FormControl>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Cancelar
          </Button>
          <Button
            colorScheme="yooga"
            onClick={handleSubmit}
            isLoading={submitting}
            isDisabled={!canSubmit}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CashbackPaymentModal;
